import { isEqual } from 'lodash'
import moment from 'moment'

import { riskScoreFieldMapping } from 'Constants'

import * as constants from 'Constants'

export const createAttribute = (name, value) => ({
  type: { name },
  createdDate: null,
  value,
})

export const formatAmount = (amount, name) => {
  return amount?.value
}

export const formatSource = (data, name) => {
  return data
}

export const formatAttribute = (fieldName) => (attributes) => {
  const attribute = attributes?.find(({ type }) => {
    return type.name === fieldName
  })
  return attribute?.value
}

export const formatCurrencyField = (value) => {
  if (Number.isNaN(value) || !value) {
    return '0.00'
  }
  // Convert to string to allow numbers to be passed
  let stripped = value.toString().replace(/[^0-9.,]/g, '') || 0
  //stripped = stripped.toString().replace(/,/g, '')
  return parseFloat(stripped).toFixed(2).toString()
}

export const formatPhoneNumber = (value) => {
  if (typeof value !== 'string') value = value.toString()
  return value.replace(/(\d{3})(\d{3})(\d{4})/, '($1)-$2-$3')
}

export const formatMultiSelect = (values) => {
  return values?.map((value) => {
    if (!value.value) {
      return { ...value, value: value.name }
    }
    return formatSelect(value)
  })
}

export const formatSelect = (option) => {
  if (!option) {
    return
  }
  return {
    label: option.label || option.name || option.value || option.description,
    value: option,
  }
}

export const getDates = (attributes) => {
  const start = attributes?.find(({ type }) => type.name === 'startdate')?.value
  const end = attributes?.find(({ type }) => type.name === 'enddate')?.value
  return { start, end }
}

export const getReduxFormSelectLabel = ({ label, value }) => {
  return label || value?.name
}

export const normalizeAmount = (value, previous) => {
  return { ...previous, value }
}

export const normalizeAttribute = (fieldName) => (value, attributes) => {
  if (attributes.filter((a) => a.type.name === fieldName)[0]) {
    return attributes.map((attribute) => {
      if (attribute.type.name === fieldName) {
        return { ...attribute, value }
      }
      return attribute
    })
  } else {
    attributes.push({ type: { name: fieldName }, value })
    return attributes
  }
}

export const normalizeMultiSelect = (items) => {
  if (!items?.map) {
    return
  }
  return items?.map((item) => {
    if (typeof item === 'string') {
      return { value: item, label: item, name: item }
    }

    return normalizeSelect(item)
  })
}

export const normalizeSelect = (option) => {
  if (!option) {
    return
  }
  const { label, value } = option
  if (typeof value === 'string') {
    return { value, label }
  }

  const normalized = { ...value, label }

  if (!normalized.value) {
    return { ...normalized, value: normalized.label }
  }
  return normalized
}

export const toISODate = (date) => {
  return new Date(date?.includes(' ') ? date?.split(' ')[0] : date).toISOString()
}

export const formatResponseAttribute = (attribute) => {
  if (attribute.isFinancialField) {
    // Ensure currency formatted to two decimal places
    return { ...attribute, value: parseFloat(attribute.value).toFixed(2) }
  }
  if (attribute.isDateField) {
    // Ensure dates are ISO strings
    return { ...attribute, value: toISODate(attribute.value) }
  }
  return { ...attribute, value: attribute.value }
}

export const formatLineItem = ({ attributes, chargeDate, total, unitPrice, ...item }) => {
  const parsedTotal = parseFloat(total)
  const tax = attributes?.find(({ type }) => {
    return type.name === 'Tax'
  })
  const parsedUnitPrice = parseFloat(unitPrice)
  const chargeDateValue =
    chargeDate?.indexOf(constants.ZERO_VALUE_CHARGE_DATE) >= 0 ? null : chargeDate
  return {
    ...item,
    chargeDate: chargeDateValue || null,
    tax: tax?.value ? parseFloat(tax.value).toFixed(2) : '0.00',
    total: Number.isNaN(parsedTotal) ? null : parsedTotal.toFixed(2),
    unitPrice: Number.isNaN(parsedUnitPrice) ? null : parsedUnitPrice.toFixed(2),
  }
}

// IDs can change on line items, so only compare by value
const attributesEqual = (currentAttributes, newAttributes) => {
  const changed = currentAttributes.map((current) => {
    const updated = newAttributes.find(({ type }) => type.name === current.type.name)
    return current.value !== updated.value
  })
  return !changed.some((item) => item)
}

const areFieldsEqual = (currentValue, newValue) => {
  if (currentValue?.name || currentValue?.value) {
    return isEqual(currentValue.name || currentValue.value, newValue.name || newValue.value)
  }
  return isEqual(currentValue, newValue)
}

// Row IDs can not be matched, but values are still the same so compare by value
const areLineItemsEqual = (currentLineItems, newLineItems) => {
  const currentWithoutIds = currentLineItems.map(({ rowId, ...item }) => {
    return item
  })
  const newWithoutIds = newLineItems.map(({ rowId, ...item }) => {
    return item
  })
  return isEqual(currentWithoutIds, newWithoutIds)
}

const areTagsEqual = (currentValue, newValue) => {
  const currentTagValues = currentValue?.map(({ name, value }) => {
    return name || value
  })
  const newTagValues = newValue?.map(({ name, value }) => {
    return name || value
  })
  return isEqual(currentTagValues || [], newTagValues || [])
}

export const expenseFieldsEqual = (currentFields, newFields) => {
  const currentEntries = Object.entries(currentFields)

  return currentEntries.reduce((acc, [key, value]) => {
    // Don't compare if there's nothing to compare, if acc is false always return false
    if (!newFields || !newFields[key] || !acc) {
      return acc
    }
    if (
      key === 'purchaseOrders' ||
      key === 'shippingDocuments' ||
      key === 'otherAttachments' ||
      key === 'workFlowItems'
    ) {
      return true
    }
    if (key === 'attributes') {
      return attributesEqual(value, newFields[key])
    }
    if (key === 'lineItems') {
      return areLineItemsEqual(value, newFields[key])
    }
    if (key === 'jobCodes' || key === 'tags') {
      return areTagsEqual(value, newFields[key])
    }
    return areFieldsEqual(value, newFields[key])
  }, true)
}

// TODO: the label param currently allows for multiple formats and should stick to one.
// Should search for exceptions first and compare them to the fields displayed rather than displayed
// fields passing in hard coded exception titles
export const getException = (label, exceptions) => {
  return exceptions?.filter((data) => {
    return riskScoreFieldMapping[data?.exception] === label || data?.exception === label
  })[0]
}

export const findExceptionByType = (exception) => {
  return constants.exceptions.filter((data) => {
    return data.type === exception?.exception
  })[0]
}

export const getErrorMessage = (isError, label, exception) => {
  return '' // as of now we are hiding the error messages in Expense Details layout
  // const exceptionData = findExceptionByType(exception)
  // if(isError){
  //   return `${exceptionData?.title}!`
  // }
  // return ''
}

export const getErrorMessageDescription = (isError, label, exception) => {
  return '' // as of now we are hiding the error messages in Expense Details layout
  // const exceptionData = findExceptionByType(exception)
  // if(isError){
  //   return `${exceptionData?.description}!`
  // }
  // return ''
}

export const getColumnsKey = (reportType) => {
  if (reportType?.type === 'expenseDetails') {
    return 'expenseColumns'
  }
  if (reportType?.type === 'auditLog') {
    return 'auditLogColumns'
  }
  if (reportType?.type === 'confidenceScore') {
    return 'confidenceScoreColumns'
  }
  return ''
}

export const formatDateSource = (data, name) => {
  return moment(data).format(constants.DATE_FORMAT)
}

export const formatAmountWithCurrency = (data, name) => {
  return '$' + parseFloat(data).toFixed(2)
}

export const formatItemizeAmountWithCurrency = (amount, currencySymbol) => {
  return `${currencySymbol}` + parseFloat(amount?.value).toFixed(2)
}

export const optionizeUsers = (managedUsers = []) =>
  managedUsers.map(({ id, name, email }) => ({
    value: id,
    label: name,
    email: email,
  }))

export const getAdminUsers = (managedUsers = [], userProfile = {}) => {
  // @TODO This really needs to be driven by the permissions of a role and not by the role itself
  return managedUsers.filter(
    (userObj) => ['admin', 'project_manager'].includes(userObj.role) && userProfile?.id !== userObj?.id,
  )
}

export const getWorkflowStepStatuses = (allWorkflowStatuses = []) => {
  return allWorkflowStatuses
    .filter((workflowStatusObj) => ['Approved', 'Rejected'].indexOf(workflowStatusObj.label) >= 0)
    .map((workFlowObj) => {
      return {
        label: constants.WORKFLOW_STEP_STATUSES[workFlowObj?.label],
        value: workFlowObj?.value?.id,
      }
    })
}
